import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import useAPIMutation from '../api/useAPIMutation';
import useAPIQuery from '../api/useAPIQuery';
import { CandidateDetail, JobPostingDetail } from '../types';
import SendCustomTestsModal, {
  SendCustomTestsFormValues,
  SendCustomTestsModalProps,
} from './SendCustomTestsModal';

interface PartialSendCustomTestsModalProps extends Omit<SendCustomTestsModalProps, 'helmetTitle'> {
  candidateIds: CandidateDetail['id'][];
  jobPostingId?: JobPostingDetail['id'];
  onSuccess?: (
    data: void,
    variables: SendCustomTestsFormValues,
    context: unknown
  ) => void | Promise<unknown>;
}

const PartialSendCustomTestsModal = ({
  candidateIds,
  onSuccess,
  jobPostingId,
  ...props
}: PartialSendCustomTestsModalProps) => {
  const { t } = useTranslation();

  const queryClient = useQueryClient();

  const user = useAPIQuery('/users/me/');

  const mutation = useAPIMutation(
    async (
      {
        message,
        surveys,
        subject,
        attachments,
        enableReplyTo,
        fromName,
        replyTo,
      }: SendCustomTestsFormValues,
      api
    ) => {
      const attachmentIds: number[] = [];

      for (const attachment of attachments || []) {
        if (attachment.originFileObj) {
          const attachmentFormData = new FormData();
          attachmentFormData.append('filename', attachment.name);
          attachmentFormData.append('file', attachment.originFileObj as File, attachment.name);
          attachmentFormData.append('mimetype', attachment.type as string);
          const attachmentResponse = await api.post('/attachments/', attachmentFormData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });

          attachmentIds.push(attachmentResponse.data.id);
        } else {
          attachmentIds.push(parseInt(attachment.uid, 10));
        }
      }

      for (const candidateId of candidateIds) {
        api.post('/email-communications/', {
          from_name: fromName,
          candidate: candidateId,
          job_posting: jobPostingId,
          subject,
          message,
          ...(enableReplyTo ? { reply_to: replyTo } : {}),
          type: 'CUSTOM_TESTS',
          custom_tests_ids: surveys,
          attachments: attachmentIds,
          created_by: user.data?.id,
        });
      }
    },
    {
      onSuccess,
      onSettled: async () => {
        await queryClient.invalidateQueries('/candidate-history/');
      },
    }
  );

  return (
    <SendCustomTestsModal
      title={
        candidateIds.length > 1 ? (
          <span>
            <Trans>Enviar testes customizados para</Trans>{' '}
            <strong>
              {candidateIds.length} <Trans>candidato(s)</Trans>
            </strong>
          </span>
        ) : (
          <span>
            <Trans>Enviar testes customizados</Trans>
          </span>
        )
      }
      jobPostingId={jobPostingId}
      helmetTitle={
        candidateIds.length > 1
          ? `${t('Enviar testes customizados para')} ${candidateIds.length} ${t('candidato(s)')}`
          : t('Enviar testes customizados')
      }
      loading={mutation.isLoading}
      onFinish={(variables) => {
        mutation.mutate(variables);
      }}
      {...props}
    />
  );
};

export default PartialSendCustomTestsModal;
